import { Box } from '@mui/joy';
import { motion } from 'framer-motion';

/**
 * Сегментированный переключатель в стиле iOS.
 *
 * Тот же элемент, что и в приложении: вкладки поиска («Чаты / Сообщения»),
 * содержимое переписки («Медиа / Файлы / Ссылки»). Активная вкладка подсвечена
 * плашкой, которая переезжает между сегментами — за переезд отвечает общий
 * `layoutId`, поэтому он обязан быть своим у каждого переключателя на странице.
 *
 * @param {string} value - выбранный вариант
 * @param {Array<{value: string, label: string, count?: number}>} options
 * @param {Function} onChange
 * @param {string} layoutId - уникальный ключ анимации плашки
 */
export default function SegmentControl({ value, options = [], onChange = () => { }, layoutId = 'segment-control', sx = {} }) {
    return (
        <Box
            role="tablist"
            sx={{
                display: 'flex',
                p: 0.5,
                gap: 0.5,
                borderRadius: '50px',
                bgcolor: 'background.level1',
                ...sx,
            }}
        >
            {options.map((option) => {
                const isActive = value === option.value;

                return (
                    <Box
                        key={option.value}
                        role="tab"
                        aria-selected={isActive}
                        tabIndex={0}
                        // Не забираем фокус у того, что сейчас в фокусе: в поиске
                        // переключатель стоит под строкой ввода, и уход фокуса
                        // сворачивал бы её раньше, чем сработает клик.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onChange(option.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onChange(option.value);
                            }
                        }}
                        sx={{
                            position: 'relative',
                            flex: 1,
                            zIndex: 1,
                            py: 0.75,
                            px: 1,
                            borderRadius: '50px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            fontSize: '14px',
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? 'text.primary' : 'text.tertiary',
                            transition: 'color .2s ease',
                            outline: 'none',
                            '&:focus-visible': {
                                boxShadow: '0 0 0 2px var(--joy-palette-primary-outlinedBorder)',
                            },
                        }}
                    >
                        {isActive && (
                            <Box
                                component={motion.div}
                                layoutId={layoutId}
                                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                sx={(theme) => ({
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: '50px',
                                    zIndex: -1,
                                    boxShadow: 'sm',
                                    backgroundColor:
                                        theme.palette.mode === 'dark'
                                            ? theme.vars.palette.primary[800]
                                            : theme.vars.palette.background.surface,
                                })}
                            />
                        )}

                        {option.label}
                        {typeof option.count === 'number' && option.count > 0 && (
                            <Box component="span" sx={{ ml: 0.5, opacity: 0.6 }}>
                                {option.count}
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
